import { useCallback } from 'react';
import { useForm } from 'react-hook-form';
import {
  dataToForm,
  formToBody,
  OtherIncomeFormValues,
  UpsertIncomeDTO,
} from './OtherIncomeForm.meta';
import { IncomeModel, IncomeType } from '../../api/useIncomeQuery';
import { useUpsertIncomeMutation } from '../../api/useUpsertIncomeMutation';
import { toast } from '../../../../CommonComponents/OPL/Toast/Toast';
import {
  ADD_INCOME_SUCCESS_MESSAGE,
  EDIT_INCOME_SUCCESS_MESSAGE,
  CLONE_INCOME_SUCCESS_MESSAGE,
} from '../../Income.meta';
import { IncomeData } from '../../useIncome';

const FIELDS_TO_SHOW_ADVANCED = ['StartAge', 'EndAge', 'Inflation', 'IsTaxFree'];

interface Params {
  clientId: string;
  income: (IncomeModel & { isClone?: boolean }) | null;
  data: IncomeData;
  onSuccess: () => void;
  readOnly?: boolean;
}

export const useOtherIncomeForm = ({ clientId, income, data, onSuccess, readOnly }: Params) => {
  const formBag = useForm<OtherIncomeFormValues>({
    defaultValues: dataToForm(income, data),
    disabled: readOnly,
  });

  const mutationBag = useUpsertIncomeMutation({ clientId });

  const defaultShowAdvanced =
    !!income &&
    (FIELDS_TO_SHOW_ADVANCED.some(field => !!income[field] || income[field] === 0) ||
      income.AccountEarmark.length > 0);

  const onSubmit = formBag.handleSubmit(async (values: OtherIncomeFormValues) => {
    const body: UpsertIncomeDTO = {
      clientId,
      ...formToBody(values),
      id: (!income?.isClone && income?.Id) || null,
      incomeType: IncomeType.Other,
    };

    try {
      await mutationBag.mutateAsync(body);

      toast.success(
        income?.Id
          ? income.isClone
            ? CLONE_INCOME_SUCCESS_MESSAGE
            : EDIT_INCOME_SUCCESS_MESSAGE
          : ADD_INCOME_SUCCESS_MESSAGE,
      );

      onSuccess();
    } catch (error) {
      console.error(error);
    }
  });

  const resetValues = useCallback(() => {
    formBag.reset(dataToForm(income, data));
  }, [formBag, income, data]);

  return {
    data: {
      formBag,
      defaultShowAdvanced,
      mutationBag,
    },
    actions: {
      onSubmit,
      resetValues,
    },
  };
};
